function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useEffect, useRef, useCallback, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { fontStyle, spacing, themeLight, radius, breakpoints } from '@naf/theme';
import { nafColor } from '@nafcore/theme';
import { ExpandMore } from '@styled-icons/material/ExpandMore';
import { propValidationConfig } from './propValidationConfig';
export const Dropdown = /*#__PURE__*/forwardRef(({
  options,
  handleSelect,
  selected,
  width = '256px',
  maxHeight,
  justify = 'left',
  className,
  ...rest
}, ref) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [mouseDown, setMouseDown] = useState(false);
  const removeOutline = () => {
    setMouseDown(true);
  };
  const addOutline = e => {
    if (e.key === 'Tab') {
      setMouseDown(false);
    }
  };
  const onSelect = option => {
    handleSelect(option);
    setOpen(false);
  };
  const handleClickOutside = e => {
    if (!!dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setOpen(false);
    }
  };
  const handleEscape = useCallback(e => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  }, []);
  useEffect(() => {
    window.addEventListener('keydown', e => {
      handleEscape(e);
      addOutline(e);
    });
    return () => {
      window.removeEventListener('keydown', e => {
        handleEscape(e);
        addOutline(e);
      });
    };
  }, [handleEscape]);
  useEffect(() => {
    window.addEventListener('mousedown', e => {
      handleClickOutside(e);
      removeOutline();
    });
    return () => {
      window.removeEventListener('mousedown', e => {
        handleClickOutside(e);
        removeOutline();
      });
    };
  }, []);
  return /*#__PURE__*/React.createElement(Container, _extends({
    ref: dropdownRef,
    "aria-expanded": open,
    "aria-haspopup": "listbox",
    className: className,
    id: mouseDown ? 'mousedown' : ''
  }, rest), /*#__PURE__*/React.createElement(ButtonWrap, {
    width: width,
    justify: justify
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => setOpen(!open),
    open: open,
    ref: ref
  }, selected.label, " ", /*#__PURE__*/React.createElement(ExpandMore, {
    size: 24
  }))), open && options && /*#__PURE__*/React.createElement(ListContainer, {
    width: width,
    maxHeight: maxHeight
  }, /*#__PURE__*/React.createElement(UL, null, options.map(option => /*#__PURE__*/React.createElement(LI, {
    key: option.value
  }, /*#__PURE__*/React.createElement(Option, {
    onClick: () => onSelect(option),
    selected: selected.value === option.value
  }, option.label))))));
});
const Container = styled.div.withConfig({
  displayName: "dropdown__Container",
  componentId: "sc-1o46htb-0"
})(["position:relative;display:inline-flex;flex-direction:column;&#mousedown *:focus{outline:0;}"]);
const ButtonWrap = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "dropdown__ButtonWrap",
  componentId: "sc-1o46htb-1"
})(["display:flex;", " ", " ", " ", ""], ({
  width
}) => width && css(["width:", ";"], width), ({
  justify
}) => justify === 'left' && css(["justify-content:flex-start;"]), ({
  justify
}) => justify === 'center' && css(["justify-content:center;"]), ({
  justify
}) => justify === 'right' && css(["justify-content:flex-end;"]));
const Button = styled.button.withConfig(propValidationConfig).withConfig({
  displayName: "dropdown__Button",
  componentId: "sc-1o46htb-2"
})(["padding:", ";padding-left:", ";border:0;background:", ";", ";border-radius:", ";display:flex;justify-content:center;align-items:center;color:", " !important;&:hover{cursor:pointer;}&:hover,&:focus{background:", ";color:", " !important;}&:active{background:", ";color:", " !important;}svg{margin-left:", ";}", ";@media (max-width:", "){", ";}"], spacing.space12, spacing.space16, ({
  theme
}) => theme.background ? theme.background.default : themeLight.background.default, fontStyle.bodyText.bodyText, radius.s, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.hover : themeLight.componentColors.interactiveElement.hover, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.active : themeLight.componentColors.interactiveElement.active, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, spacing.space12, ({
  open
}) => open && css(["background:", ";&:focus{background:", ";}svg{transform:scaleY(-1);}"], ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.active : themeLight.componentColors.interactiveElement.active, ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.active : themeLight.componentColors.interactiveElement.active), breakpoints.s, fontStyle.bodyText.small);
const ListContainer = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "dropdown__ListContainer",
  componentId: "sc-1o46htb-3"
})(["position:absolute;top:", ";background:", ";box-shadow:0 2px 12px rgba(0,0,0,0.24);border-radius:", ";overflow-y:auto;z-index:99;", " ", ""], spacing.space56, ({
  theme
}) => theme.background ? theme.background.default : themeLight.background.default, radius.s, ({
  width
}) => width && css(["width:", ";"], width), ({
  maxHeight
}) => maxHeight && css(["max-height:calc(", " * ", ");"], spacing.space48, maxHeight));
const UL = styled.ul.withConfig({
  displayName: "dropdown__UL",
  componentId: "sc-1o46htb-4"
})(["padding:0;list-style:none;margin:0;"]);
const LI = styled.li.withConfig({
  displayName: "dropdown__LI",
  componentId: "sc-1o46htb-5"
})(["padding:0;list-style:none;"]);
const Option = styled.button.withConfig({
  displayName: "dropdown__Option",
  componentId: "sc-1o46htb-6"
})(["width:100%;text-align:start;white-space:nowrap;border:0;padding:0 ", ";box-sizing:border-box;appearance:none;height:", ";background:transparent;", ";color:", " !important;&:hover{cursor:pointer;}&:hover,&:focus{background:", ";color:", " !important;}"], spacing.space12, spacing.space48, fontStyle.bodyText.bodyText, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, nafColor.signature.yellow10, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText);
//# sourceMappingURL=dropdown.js.map